import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useDefaultColor } from 'customHooks/useDefaultColor';
import {
    Campaign,
    WidgetIcon,
    WidgetIconPosition,
    WidgetSignupFormChannel,
    WidgetSignupFormLayout,
    WidgetType,
} from 'types/domain';

interface WidgetLivePreviewProps {
    type?: WidgetType;
    campaign?: Campaign | null;
    backgroundColor?: string;
    borderWidth?: string;
    borderColor?: string;
    borderRadius?: string;
    label?: string;
    labelColor?: string;
    icon?: WidgetIcon;
    iconPosition?: WidgetIconPosition;
    dropShadow?: boolean;
    goalBarType?: string;
    textColor?: string;
    progressBarColor?: string;
    size?: string;
    showGoalAmount?: boolean;
    showRaisedAmount?: boolean;
    showRaisedPercentage?: boolean;
    showGoalBar?: boolean;
    title?: string;
    description?: string;
    layout?: WidgetSignupFormLayout;
    buttonText?: string;
    buttonTextColor?: string;
    buttonBackgroundColor?: string;
    channel?: WidgetSignupFormChannel;
    image?: string | null;
    imageSizing?: string;
}
const WidgetLivePreview = (props: WidgetLivePreviewProps) => {
    const defaultColor = useDefaultColor();
    const {
        // common
        type = 'button',
        campaign,
        backgroundColor,
        borderColor,
        borderRadius,
        borderWidth,
        // button
        label,
        labelColor,
        icon,
        iconPosition,
        dropShadow,
        // goal bar
        goalBarType,
        textColor,
        progressBarColor,
        size,
        showGoalAmount,
        showRaisedAmount,
        showRaisedPercentage,
        // giving form
        showGoalBar,
        // signup form
        channel,
        layout,
        title,
        description,
        buttonText,
        buttonTextColor,
        buttonBackgroundColor,
        image,
        imageSizing,
    } = props;
    const { accountAid } = useCurrentAccount();
    const campaignThemeColor = campaign?.settings.find(
        item =>
            item.code === Settings.CampaignSpecificThemeColor.Code &&
            item.name === Settings.CampaignSpecificThemeColor.Name,
    );

    if (type === 'giving-form') {
        return (
            <div className={'pointer-events-none w-full p-4'}>
                <givebutter-giving-form
                    account={accountAid}
                    campaign={campaign?.code}
                    show-goal-bar={showGoalBar ?? false}
                    theme-color={campaignThemeColor?.value ?? defaultColor}
                ></givebutter-giving-form>
            </div>
        );
    }

    if (type === 'signup-form') {
        return (
            <div className={'pointer-events-none w-full p-4'}>
                <givebutter-signup-form
                    account={accountAid}
                    id={'signup-form-demo'}
                    type="static"
                    channel={channel}
                    border-radius={borderRadius}
                    title={title}
                    description={description}
                    layout={layout}
                    button-text={buttonText}
                    button-text-color={buttonTextColor}
                    button-background-color={buttonBackgroundColor}
                    image={image}
                    image-sizing={imageSizing}
                ></givebutter-signup-form>
            </div>
        );
    }

    if (type === 'goal-bar') {
        return (
            <div className={'pointer-events-none w-full p-4'}>
                <givebutter-goal-bar
                    account={accountAid}
                    campaign={campaign?.code}
                    background-color={backgroundColor ?? '#FFF'}
                    border-width={borderWidth ?? '1'}
                    border-color={borderColor ?? '#E2E4E5'}
                    border-radius={borderRadius ?? '16'}
                    text-color={textColor ?? '#000'}
                    progress-bar-color={progressBarColor ?? '#50C878'}
                    size={size}
                    type={goalBarType}
                    show-goal-amount={showGoalAmount ?? true}
                    show-raised-amount={showRaisedAmount ?? true}
                    show-raised-percentage={showRaisedPercentage ?? true}
                    {...(campaign && !campaign?.goal
                        ? { goal: 1000, raised: 500, 'raised-percentage': 50 }
                        : {})}
                ></givebutter-goal-bar>
            </div>
        );
    }

    return (
        <div className={'pointer-events-none'}>
            <givebutter-button
                account={accountAid}
                campaign={campaign?.code}
                label={label ?? 'Donate'}
                background-color={backgroundColor ?? defaultColor}
                border-width={borderWidth ?? ''}
                border-color={borderColor ?? ''}
                label-color={labelColor ?? '#fff'}
                border-radius={borderRadius ?? '24'}
                icon={icon ?? 'heart'}
                icon-position={iconPosition ?? 'left'}
                drop-shadow={dropShadow ?? true}
            ></givebutter-button>
        </div>
    );
};

export { WidgetLivePreview };
